/**
 * پرتابِ تاسِ منچ روی تخته (کاربر، ۲۵ اکتبر ۲۰۲۶): «بازیکن درگیرِ تاس باشد» — تاس به سوی کشیدنِ انگشت می‌افتد،
 * روی تخته می‌غلتد، به قاب می‌خورد و برمی‌گردد، و روی عددِ ریخته‌شده می‌ایستد. همتای DiceThrow.cs ِ اپ.
 *
 * عدد از پیش معلوم است (سرور، یا هسته‌ی آفلاین)؛ این‌جا فقط راهِ رسیدن به آن است:
 *   • مسیر (throwPath) یک‌جا از پیش حساب می‌شود: سرعت از (۱ − t/T)² کم می‌شود تا درست سرِ T بایستد؛ هر
 *     برخورد با دیوار (قاب) سوی آن محور را برمی‌گرداند و سرعت را ۰٫۷ می‌کند.
 *   • چرخش غلتیدنِ واقعی است: محورِ عمود بر سوی حرکت در سطحِ تخته، زاویه = راه ÷ نیم‌ضلع؛ در بخشِ پایانی
 *     از چرخشِ غلتان به چرخشی که روی عدد را رو به بیننده می‌آورد نرم می‌رود (slerp) — با کمترین پیچ.
 *
 * واحد: خانه‌ی تخته (۰..۱۵، y رو به پایین، همان menchXY). چرخش‌ها کواترنیون در قابِ CSS (x راست، y پایین،
 * z رو به بیننده) — همان قابی که rotateX/rotateY ِ مکعبِ Die3D در آن‌اند.
 */

/** نیم‌ضلعِ تاس روی تخته (خانه) — دیوارها همین‌قدر از لبه‌ی زمینِ ۱۵ خانه‌ای فاصله دارند. */
export const THROW_R = 0.75;
/** کمترین و بیشترین تندیِ پرتاب (خانه بر ثانیه). */
export const THROW_MIN = 7, THROW_MAX = 24;

export type ThrowSample = { t: number; x: number; y: number; dx: number; dy: number; roll: number };
export type ThrowPath = { samples: ThrowSample[]; bounces: number[]; T: number };

/**
 * زمینِ پرتاب به واحدِ پرتاب: پهنا و بلندی. منچ و مارپله مربعِ ۱۵ × ۱۵ اند؛ تخته‌ی تخته‌نرد پهن است (nardThrowField
 * در BoardTable.tsx) — همان فیزیک، فقط دیوارهای دیگر.
 */
export type ThrowField = { w: number; h: number };
export const FIELD15: ThrowField = { w: 15, h: 15 };

/** نقطه‌ی آغاز: کنارِ لبه‌ی تخته، روبه‌روی جعبه‌ی تاسِ گوشه‌ی محلیِ c (۰ پایین-چپ، ۱ بالا-چپ، ۲ بالا-راست، ۳ پایین-راست). */
export function throwStart(corner: number, f: ThrowField = FIELD15): [number, number] {
  const left = corner === 0 || corner === 1, top = corner === 1 || corner === 2;
  return [left ? 3.5 : f.w - 3.5, top ? 1.1 : f.h - 1.1];
}

/**
 * سرعتِ آغاز: کشیدنِ انگشت (خانه بر ثانیه) اگر بود — رو به درونِ تخته برگردانده و در بازه‌ی تندی؛ وگرنه
 * (لمس، یا تاسِ دیگران) رو به وسط با کمی کجی. rnd ۰..۱ برای کجی و تندی.
 */
export function throwVelocity(corner: number, swipe: [number, number] | null, rnd: () => number = Math.random,
                              f: ThrowField = FIELD15): [number, number] {
  const [sx, sy] = throwStart(corner, f);
  const cx = f.w / 2, cy = f.h / 2;
  const inY = sy < cy ? 1 : -1;
  let vx: number, vy: number;
  if (swipe && Math.hypot(swipe[0], swipe[1]) > 0.5) {
    [vx, vy] = swipe;
    // انگشت رو به بیرون کشیده شد: همان سو ولی رو به تخته (قاب پشتِ جعبه است)
    if (Math.sign(vy) !== inY && Math.abs(vy) > 0.01) vy = -vy;
    if (Math.abs(vy) < 0.2 * Math.hypot(vx, vy)) vy = inY * 0.2 * Math.hypot(vx, vy);
  } else {
    const a = Math.atan2(cy - sy, cx - sx) + (rnd() - 0.5) * 0.9;
    const sp = 12 + rnd() * 6;
    [vx, vy] = [Math.cos(a) * sp, Math.sin(a) * sp];
  }
  const sp = Math.hypot(vx, vy);
  const k = Math.min(THROW_MAX, Math.max(THROW_MIN, sp)) / (sp || 1);
  return [vx * k, vy * k];
}

/**
 * مسیرِ پرتاب از (x0، y0) با سرعتِ (vx، vy) در T ثانیه؛ نمونه‌ها هر ۱/۲۴۰ ثانیه. دیوارها: x در [lo، hi] و y در
 * [lo، hiY] (پیش‌فرض همان hi — زمینِ مربع).
 */
export function throwPath(x0: number, y0: number, vx: number, vy: number, T: number, lo = THROW_R, hi = 15 - THROW_R, hiY = hi): ThrowPath {
  const dt = 1 / 240;
  const n = Math.max(2, Math.ceil(T / dt));
  const v0 = Math.hypot(vx, vy) || 1;
  let dx = vx / v0, dy = vy / v0, x = x0, y = y0, k = 1, roll = 0;
  const samples: ThrowSample[] = [{ t: 0, x, y, dx, dy, roll }];
  const bounces: number[] = [];
  for (let i = 1; i <= n; i++) {
    const t = Math.min(T, i * dt), u = 1 - t / T;
    const step = v0 * k * u * u * dt;
    x += dx * step; y += dy * step; roll += step;
    let hit = false;
    if (x < lo) { x = 2 * lo - x; dx = -dx; hit = true; } else if (x > hi) { x = 2 * hi - x; dx = -dx; hit = true; }
    if (y < lo) { y = 2 * lo - y; dy = -dy; hit = true; } else if (y > hiY) { y = 2 * hiY - y; dy = -dy; hit = true; }
    if (hit) { k *= 0.7; bounces.push(t); }
    samples.push({ t, x, y, dx, dy, roll });
  }
  return { samples, bounces, T };
}

/** نمونه‌ی مسیر در زمانِ t (خطی میانِ دو نمونه). */
export function sampleAt(path: ThrowPath, t: number): ThrowSample {
  const s = path.samples;
  if (t <= 0) return s[0]!;
  if (t >= path.T) return s[s.length - 1]!;
  const f = (t / path.T) * (s.length - 1), i = Math.floor(f), a = s[i]!, b = s[Math.min(s.length - 1, i + 1)]!, w = f - i;
  return { t, x: a.x + (b.x - a.x) * w, y: a.y + (b.y - a.y) * w, dx: b.dx, dy: b.dy, roll: a.roll + (b.roll - a.roll) * w };
}

/** بلندیِ تاس از تخته (به ضلع) در کسرِ p ِ پرتاب: از دست می‌افتد، یک جهشِ کوتاه، بعد می‌غلتد. */
export function liftAt(p: number): number {
  if (p < 0.22) { const q = p / 0.22; return 1.1 * (1 - q * q); }
  if (p < 0.38) { const q = (p - 0.22) / 0.16; return 0.28 * Math.sin(q * Math.PI); }
  return 0;
}

// ---------------------------------------------------------------- کواترنیون (w، x، y، z)

export type Quat = [number, number, number, number];

export const qAxis = (ax: number, ay: number, az: number, a: number): Quat => {
  const l = Math.hypot(ax, ay, az) || 1, s = Math.sin(a / 2);
  return [Math.cos(a / 2), (ax / l) * s, (ay / l) * s, (az / l) * s];
};

export const qMul = (a: Quat, b: Quat): Quat => [
  a[0] * b[0] - a[1] * b[1] - a[2] * b[2] - a[3] * b[3],
  a[0] * b[1] + a[1] * b[0] + a[2] * b[3] - a[3] * b[2],
  a[0] * b[2] - a[1] * b[3] + a[2] * b[0] + a[3] * b[1],
  a[0] * b[3] + a[1] * b[2] - a[2] * b[1] + a[3] * b[0],
];

const qDot = (a: Quat, b: Quat) => a[0] * b[0] + a[1] * b[1] + a[2] * b[2] + a[3] * b[3];

/** q بردارِ v را می‌چرخاند. */
export function qRotate(q: Quat, v: [number, number, number]): [number, number, number] {
  const p = qMul(qMul(q, [0, v[0], v[1], v[2]]), [q[0], -q[1], -q[2], -q[3]]);
  return [p[1], p[2], p[3]];
}

export function qSlerp(a: Quat, b: Quat, t: number): Quat {
  let d = qDot(a, b);
  const bb: Quat = d < 0 ? [-b[0], -b[1], -b[2], -b[3]] : b;
  d = Math.abs(d);
  if (d > 0.9995) {
    const r: Quat = [a[0] + (bb[0] - a[0]) * t, a[1] + (bb[1] - a[1]) * t, a[2] + (bb[2] - a[2]) * t, a[3] + (bb[3] - a[3]) * t];
    const l = Math.hypot(...r);
    return [r[0] / l, r[1] / l, r[2] / l, r[3] / l];
  }
  const th = Math.acos(d), s = Math.sin(th), wa = Math.sin((1 - t) * th) / s, wb = Math.sin(t * th) / s;
  return [a[0] * wa + bb[0] * wb, a[1] * wa + bb[1] * wb, a[2] * wa + bb[2] * wb, a[3] * wa + bb[3] * wb];
}

/** CSS ِ چرخشِ q: rotate3d(محور، زاویه). */
export function qCss(q: Quat): string {
  const w = Math.max(-1, Math.min(1, q[0])), a = 2 * Math.acos(w), s = Math.sqrt(1 - w * w);
  if (s < 1e-6) return "rotate3d(0, 0, 1, 0rad)";
  return `rotate3d(${(q[1] / s).toFixed(5)}, ${(q[2] / s).toFixed(5)}, ${(q[3] / s).toFixed(5)}, ${a.toFixed(5)}rad)`;
}

const deg = Math.PI / 180;
/** رو‌های مکعبِ Die3D: rotateX(fx) rotateY(fy) روی v را رو به بیننده می‌آورد (همان FACE ِ Dice3D.tsx). */
const FACE: Record<number, [number, number]> = { 1: [0, 0], 6: [0, 180], 2: [-90, 0], 5: [90, 0], 3: [0, -90], 4: [0, 90] };
/** کجیِ کمِ تاسِ ایستاده، تا دو روی کناری هم پیدا باشند (همان TILT ِ Die3D). */
const TILT: Quat = qMul(qAxis(1, 0, 0, -14 * deg), qAxis(0, 1, 0, 12 * deg));

/** چرخشِ ایستاده روی v با پیچِ yaw (رادیان) دورِ محورِ نگاه. */
export function qRest(v: number, yaw: number): Quat {
  const [fx, fy] = FACE[v] ?? FACE[1]!;
  return qMul(TILT, qMul(qAxis(0, 0, 1, yaw), qMul(qAxis(1, 0, 0, fx * deg), qAxis(0, 1, 0, fy * deg))));
}

/** از میانِ پیچ‌های هر ۱۵ درجه، ایستاده‌ای روی v که به q نزدیک‌تر است (کمترین چرخشِ پایانی). */
export function qRestNear(v: number, q: Quat): Quat {
  let best = qRest(v, 0), bd = -1;
  for (let i = 0; i < 24; i++) {
    const r = qRest(v, (i * 15) * deg), d = Math.abs(qDot(r, q));
    if (d > bd) { bd = d; best = r; }
  }
  return best;
}

/** یک گامِ غلتیدن: سوی (dx، dy) به اندازه‌ی ds خانه — محورِ z × سو، زاویه ds ÷ THROW_R. */
export const qRoll = (q: Quat, dx: number, dy: number, ds: number): Quat => qMul(qAxis(-dy, dx, 0, ds / THROW_R), q);

/** کسرِ پایانیِ پرتاب که چرخش به سوی عدد نرم می‌رود (روشِ قدیم؛ پرتاب حالا planRoll/rollAt است). */
export const SETTLE_FROM = 0.66;

// ---------------------------------------------------------------- غلتِ مکعب

/*
 * «آخرِ تاس که عدد را نشان می‌دهد باید نرم و واقعی باشد» (کاربر، ۲۵ اکتبر ۲۰۲۶). پیش‌تر تاس مثلِ گوی می‌غلتید و در
 * یک‌سومِ پایانی درجا به سوی رویِ عدد می‌چرخید (slerp) — چرخشی که هیچ تاسی نمی‌کند. حالا:
 *   • در هوا (تا فرودِ اول، LAND_AT) دورِ یک محور می‌چرخد و درست سرِ فرود صاف روی یک رو می‌نشیند (F0)؛
 *   • روی تخته لبه‌به‌لبه می‌غلتد: هر غلت ربع‌دور حولِ لبه‌ی پایینیِ رو به حرکت است و مرکز را یک ضلع جلو می‌برد؛
 *     مرکز سرِ لبه بالا می‌آید (tipRise)، روی لبه کند می‌شود و در افتادن تند (tipShape) — و غلتِ آخر صاف روی
 *     عدد می‌افتد، کمی پس می‌نشیند و می‌ایستد؛
 *   • F0 از آخر به اول حساب می‌شود (جهتِ پایانی × وارونه‌ی همه‌ی غلت‌ها)، پس عدد هیچ‌وقت با چرخشِ جعلی نمی‌آید.
 * جابه‌جایی هم پا‌به‌پای غلت است (طولِ راه روی همان مسیرِ throwPath)، تا تاس بی‌چرخش سُر نخورد.
 * چرخش‌ها «فیزیکی» اند (بی کجیِ دوربین)؛ نما TILT را رویشان می‌گذارد.
 */

type Vec3 = [number, number, number];

/** ضلعِ تاس روی تخته (خانه): هر غلت (ربع‌دور حولِ لبه) مرکز را یک ضلع جلو می‌برد. */
export const DIE_SIDE = 2 * THROW_R;
/** فرودِ اول، کسرِ T — تا این‌جا تاس در هواست (liftAt). */
export const LAND_AT = 0.22;
/** پس‌نشستنِ کوتاهِ بعد از غلتِ آخر (ثانیه). */
export const SETTLE_WOBBLE = 0.16;

export type RollTip = { t0: number; dur: number; axis: Vec3; from: Quat };
export type RollPlan = {
  path: ThrowPath;
  tLand: number;
  /** پایانِ غلتِ آخر — از این‌جا تاس روی عدد ایستاده است (پس‌نشستن تا SETTLE_WOBBLE). */
  tEnd: number;
  airAxis: Vec3;
  airSpin: number;
  /** جهتِ فیزیکیِ سرِ فرود: صاف، یک رو پایین. */
  F0: Quat;
  tips: RollTip[];
  /** جهتِ فیزیکیِ پایانی: رویِ value رو به بیننده. */
  end: Quat;
  dLand: number;
  D: number;
};

export const qConj = (q: Quat): Quat => [q[0], -q[1], -q[2], -q[3]];

/** چرخشی که رویِ v را رو به بیننده (+z) می‌آورد، بی کجی و بی پیچ. */
export function faceUp(v: number): Quat {
  const [fx, fy] = FACE[v] ?? FACE[1]!;
  return qMul(qAxis(1, 0, 0, fx * deg), qAxis(0, 1, 0, fy * deg));
}

/** نما: کجیِ دوربین روی جهتِ فیزیکی. */
export const qView = (p: Quat): Quat => qMul(TILT, p);

/** نمونه‌ی مسیر در طولِ راهِ d (همان roll ِ نمونه‌ها) — جست‌وجوی دودویی. */
export function sampleAtDist(path: ThrowPath, d: number): ThrowSample {
  const s = path.samples;
  if (d <= s[0]!.roll) return s[0]!;
  const last = s[s.length - 1]!;
  if (d >= last.roll) return last;
  let lo = 0, hi = s.length - 1;
  while (hi - lo > 1) {
    const mid = (lo + hi) >> 1;
    if (s[mid]!.roll < d) lo = mid; else hi = mid;
  }
  const a = s[lo]!, b = s[hi]!, w = (d - a.roll) / Math.max(1e-9, b.roll - a.roll);
  return { t: a.t + (b.t - a.t) * w, x: a.x + (b.x - a.x) * w, y: a.y + (b.y - a.y) * w, dx: b.dx, dy: b.dy, roll: d };
}

/** پیشرفتِ یک غلت در کسرِ زمانِ f: تند از ضربه‌ی قبل، کند روی لبه (مرکز بالاست)، تند در افتادن. */
export const tipShape = (f: number): number => f + 0.12 * Math.sin(2 * Math.PI * f);

/** بالاآمدنِ مرکز (به ضلع) وقتی مکعب phi رادیان روی لبه چرخیده — بیشینه سرِ ۴۵ درجه. */
export const tipRise = (phi: number): number => (Math.SQRT2 * Math.cos(Math.PI / 4 - phi) - 1) / 2;

/**
 * نقشه‌ی غلت برای مسیرِ path که روی value می‌ایستد. شمارِ غلت از راهِ مانده پس از فرود (هر ضلع یکی، ۱ تا ۵)؛ هر
 * غلت ۱٫۳ برابرِ قبلی طول می‌کشد (انرژی می‌رود) و همه پیش از T تمام می‌شوند. سوی هر غلت: لبه‌ی پایینی‌ای که رو به
 * حرکتِ همان لحظه است (پیچِ تاس، yaw، در غلت عوض نمی‌شود).
 */
export function planRoll(path: ThrowPath, value: number, rnd: () => number = Math.random): RollPlan {
  const T = path.T, tLand = LAND_AT * T;
  const dLand = sampleAt(path, tLand).roll, D = Math.max(0, path.samples[path.samples.length - 1]!.roll - dLand);
  const n = Math.max(1, Math.min(5, Math.round(D / DIE_SIDE)));
  let geo = 0;
  for (let i = 0; i < n; i++) geo += 1.3 ** i;
  const base = Math.min(0.17 * (T / 0.9), ((T - tLand) * 0.9) / geo);
  const yaw = rnd() * Math.PI * 2;
  const rz = qAxis(0, 0, 1, yaw);
  const ex = qRotate(rz, [1, 0, 0]), ey = qRotate(rz, [0, 1, 0]);
  const edges: [number, number][] = [[ex[0], ex[1]], [-ex[0], -ex[1]], [ey[0], ey[1]], [-ey[0], -ey[1]]];
  const axes: Vec3[] = [];
  for (let i = 0; i < n; i++) {
    const m = sampleAtDist(path, dLand + (D * i) / n);
    let best = edges[0]!, bd = -Infinity;
    for (const e of edges) { const d = e[0] * m.dx + e[1] * m.dy; if (d > bd) { bd = d; best = e; } }
    axes.push([-best[1], best[0], 0]);   // محورِ غلت رو به (dx، dy) = ẑ × سو — همان qRoll
  }
  let all: Quat = [1, 0, 0, 0];
  for (const ax of axes) all = qMul(qAxis(ax[0], ax[1], ax[2], Math.PI / 2), all);
  const end = qMul(rz, faceUp(value));
  const F0 = qMul(qConj(all), end);
  const tips: RollTip[] = [];
  let t = tLand, from = F0;
  for (let i = 0; i < n; i++) {
    const dur = base * 1.3 ** i, ax = axes[i]!;
    tips.push({ t0: t, dur, axis: ax, from });
    from = qMul(qAxis(ax[0], ax[1], ax[2], Math.PI / 2), from);
    t += dur;
  }
  const a = rnd() * Math.PI * 2, z = (rnd() - 0.5) * 0.6;
  const l = Math.hypot(1, z);
  return {
    path, tLand, tEnd: t, airAxis: [Math.cos(a) / l, Math.sin(a) / l, z / l], airSpin: Math.PI * 2 * (1.25 + rnd()),
    F0, tips, end, dLand, D,
  };
}

/**
 * حالِ تاس در زمانِ t: جا (خانه)، سو، چرخشِ **نما** (با کجیِ دوربین)، بلندی (به ضلع)، شمارِ غلت‌های تمام‌شده و
 * طولِ راه (برای صدای برخورد با قاب).
 */
export function rollAt(p: RollPlan, t: number): { x: number; y: number; q: Quat; lift: number; tips: number; s: number } {
  const T = p.path.T, n = p.tips.length;
  if (t < p.tLand) {
    const sm = sampleAt(p.path, t);
    const P = qMul(qAxis(p.airAxis[0], p.airAxis[1], p.airAxis[2], p.airSpin * (1 - t / p.tLand)), p.F0);
    return { x: sm.x, y: sm.y, q: qView(P), lift: liftAt(t / T), tips: 0, s: sm.roll };
  }
  if (t >= p.tEnd) {
    const sm = sampleAtDist(p.path, p.dLand + p.D);
    const tau = t - p.tEnd;
    let P = p.end;
    if (tau < SETTLE_WOBBLE) {
      // پس‌نشستن: کمی رو به عقب (خلافِ غلتِ آخر) و برگشت
      const ax = p.tips[n - 1]!.axis, w = tau / SETTLE_WOBBLE;
      P = qMul(qAxis(ax[0], ax[1], ax[2], -0.09 * Math.sin(Math.PI * w) * (1 - w)), p.end);
    }
    return { x: sm.x, y: sm.y, q: qView(P), lift: 0, tips: n, s: sm.roll };
  }
  let i = 0;
  while (i < n - 1 && t >= p.tips[i + 1]!.t0) i++;
  const tip = p.tips[i]!;
  const g = tipShape(Math.min(1, Math.max(0, (t - tip.t0) / tip.dur)));
  const phi = (Math.PI / 2) * g;
  const P = qMul(qAxis(tip.axis[0], tip.axis[1], tip.axis[2], phi), tip.from);
  const s = p.dLand + (p.D * (i + g)) / n;
  const sm = sampleAtDist(p.path, s);
  return { x: sm.x, y: sm.y, q: qView(P), lift: liftAt(t / T) + tipRise(phi), tips: i, s };
}
